import Seo from "../components/Seo";
import { CalendarDays, CalendarHeart, FileText, Image as ImageIcon, MapPin, Megaphone, PartyPopper } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const FEE_UPDATE_PDF_URL = "/fee-update-dec-2026.pdf";
const FETE_FLYER_URL = "/fete-flyer.png";

const feeDec = [
  { session: "After School Care", permanent: "$40.00", casual: "$42.00" },
  { session: "Before School Care", permanent: "$28.00", casual: "$30.00" },
  { session: "School Vacation Care", permanent: "$115 per day", casual: "-" },
];

const feeTerm1 = [
  { session: "After School Care", permanent: "$42.00", casual: "$44.00" },
  { session: "Before School Care", permanent: "$30.00", casual: "$32.00" },
  { session: "School Vacation Care", permanent: "$115 per day", casual: "-" },
];

const FeeTable = ({ caption, rows, testId }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={testId}>
    <p className="bg-surface px-5 py-3 text-sm font-bold uppercase tracking-wider text-navy">{caption}</p>
    <table className="w-full text-left text-sm sm:text-base">
      <thead>
        <tr className="border-b border-slate-200 text-slate-500">
          <th className="px-5 py-3 font-semibold">Session</th>
          <th className="px-5 py-3 font-semibold">Permanent</th>
          <th className="px-5 py-3 font-semibold">Casual</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.session} className="border-b border-slate-100 last:border-0">
            <td className="px-5 py-3 font-semibold text-navy">{r.session}</td>
            <td className="px-5 py-3 text-slate-600">{r.permanent}</td>
            <td className="px-5 py-3 text-slate-600">{r.casual}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Announcements = () => (
  <div data-testid="announcements-page">
    <Seo
      title="Announcements & News | Telopea Park OSHC"
      description="The latest news and announcements from Telopea Park OSHC, including upcoming fee updates and community events in Barton, Canberra."
      path="/announcements"
    />
    <PageHeader
      testId="announcements-header"
      eyebrow="Announcements"
      title="News & updates for our families"
      description="The latest notices from Telopea Park OSHC - from fee updates to community events. Check back here for the most current information."
    />

    <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-24 space-y-10" data-testid="announcements-list">
      <FadeIn>
        <article className="rounded-2xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm" data-testid="announcement-fee-update">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-waratah-50 text-waratah">
              <Megaphone className="h-6 w-6" />
            </span>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-waratah">Posted 19 October 2026</p>
          </div>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-navy">
            Update on TPSOSHC fees - December 2026 and January 2027
          </h2>
          <div className="mt-4 space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            <p>
              Dear Families, we are writing to provide an update regarding upcoming fee changes at Telopea Park School
              Outside School Hours Care (TPSOSHC), with changes commencing in December 2026 and January 2027.
            </p>
            <p>
              As the Australian Government&rsquo;s Worker Retention Payment (WRP) arrangements conclude on 30 November
              2026, TPSOSHC must progressively adjust its fees to reflect increasing operational costs and support the
              long-term financial sustainability of the service. To soften the impact, we have adopted a staged approach
              rather than one substantial increase.
            </p>
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            <FeeTable caption="From 1 December 2026 (Week 8, Term 4)" rows={feeDec} testId="fee-table-dec-2026" />
            <FeeTable caption="From Term 1, 2027" rows={feeTerm1} testId="fee-table-term1-2027" />
          </div>

          <p className="mt-5 text-sm text-slate-500">
            All fees listed are before any applicable Child Care Subsidy (CCS). Eligible families may receive CCS towards
            their fees depending on their individual entitlements. As a P&amp;C-operated, not-for-profit service, any
            operating surplus is reinvested into the service and school community.
          </p>

          <a
            href={FEE_UPDATE_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fee-update-pdf-link"
            className="mt-6 inline-flex items-center gap-1.5 rounded-full border-2 border-navy px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
          >
            <FileText className="h-4 w-4" /> Read the full fee update letter (PDF)
          </a>
        </article>
      </FadeIn>

      <FadeIn delay={0.08}>
        <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:grid md:grid-cols-5" data-testid="announcement-fete">
          <a
            href={FETE_FLYER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fete-flyer-link"
            className="block md:col-span-2"
          >
            <img
              src={FETE_FLYER_URL}
              alt="Telopea Park OSHC at La Grande Fete - Saturday 31 October, 10am to 3pm, 25 New South Wales Cres, Barton ACT"
              loading="lazy"
              className="h-72 w-full object-cover md:h-full"
              data-testid="fete-flyer-image"
            />
          </a>
          <div className="p-8 sm:p-10 md:col-span-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy-50 text-navy">
                <PartyPopper className="h-6 w-6" />
              </span>
              <p className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.18em] text-waratah">
                <CalendarHeart className="h-4 w-4" /> Saturday 31 October 2026
              </p>
            </div>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-navy">
              We&rsquo;re at La Grande F&ecirc;te!
            </h2>
            <div className="mt-4 space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                Telopea Park OSHC will have a stall at <strong className="text-navy">La Grande F&ecirc;te</strong>. Come
                and say hello, <strong className="text-navy">make your own trick-or-treat bag</strong> and ask us any
                questions at our information stall.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3">
                  <CalendarDays className="mt-1 h-5 w-5 flex-none text-waratah" />
                  <span><strong className="text-navy">Saturday 31 October</strong>, 10am &ndash; 3pm</span>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 flex-none text-waratah" />
                  <span>25 New South Wales Cres, Barton ACT</span>
                </li>
              </ul>
            </div>
            <a
              href={FETE_FLYER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fete-flyer-view-link"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full border-2 border-navy px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <ImageIcon className="h-4 w-4" /> View the fete flyer
            </a>
          </div>
        </article>
      </FadeIn>
    </section>
  </div>
);

export default Announcements;
